import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

export interface UseResizeOptions {
	/**
	 * Minimum height in pixels (defaults to 350px)
	 */
	minHeight?: number;
	/**
	 * Maximum height in pixels. Overrides the default calculated max height.
	 */
	maxHeight?: number;
	/**
	 * Pixels reserved at the top of the viewport (e.g. a fixed nav bar). Subtracted from the calculated max height.
	 */
	topOffset?: number;
	/**
	 * Pixels reserved at the bottom of the viewport. Subtracted from the calculated max height.
	 * Pass 0 for bottom-anchored modals that grow upward. Defaults to HEIGHT_SAFETY_MARGIN.
	 */
	bottomOffset?: number;
	/**
	 * Minimum width in pixels (defaults to 439px to match Chat sidebar)
	 */
	minWidth?: number;
	/**
	 * Maximum width in pixels (defaults to 800px)
	 */
	maxWidth?: number;
	/**
	 * Left offset in pixels to account for fixed sidebars when calculating max width (defaults to 0)
	 */
	leftOffset?: number;
	/**
	 * Height in pixels of collapsible content (e.g., greeting header) that should be
	 * excluded from the default minimum height calculation. When provided, allows users
	 * to resize below the initial height by subtracting this value from the captured minimum.
	 */
	greetingHeight?: number;
	/**
	 * Unique identifier for the current greeting context (e.g., 'custom', 'default').
	 *
	 * When this value changes (e.g., user switches between greeting types), the resize state
	 * is reset (hasUserResized, height, defaultMinHeightRef) so the greeting can display
	 * with its default visibility instead of persisting the previous context's resize state.
	 */
	greetingKey?: string;
	/**
	 * When false, disables resize behavior for the mini modal.
	 * @default true
	 */
	enableMiniModalResize?: boolean;
}

/**
 * Arguments accepted by {@link UseResizeReturn.setSize}.
 *
 * Used by callers (e.g. the mini-modal auto-expand trigger) that want to grow
 * the modal toward a known target size while honouring the don't-shrink rule.
 */
export interface SetSizeArgs {
	/** Desired target width in pixels. The modal will grow toward this value. */
	targetWidth: number;
	/** Desired target height in pixels. The modal will grow toward this value. */
	targetHeight: number;
	/**
	 * Free-form label for the caller, surfaced back through {@link SetSizeResult}
	 * so callers can attribute analytics events without re-deriving the cause.
	 */
	source?: string;
	/**
	 * When true, computes the result (including `widthGrew`/`heightGrew`) using
	 * the exact same logic as a real call but applies no state changes. Lets a
	 * caller ask "would this resize have grown the modal?" without mutating it.
	 */
	dryRun?: boolean;
}

/**
 * Result returned from {@link UseResizeReturn.setSize}.
 *
 * Reports what the resize hook actually did, so callers (e.g. analytics) can
 * tell the difference between "we grew the modal", "we no-op'd because the
 * modal was already large enough", and "the user's viewport forced us to clamp
 * below the requested target" — all without re-running the math themselves.
 */
export interface SetSizeResult {
	/** The width applied to the wrapper (post don't-shrink + viewport clamp). */
	appliedWidth: number;
	/** The height applied to the wrapper (post don't-shrink + viewport clamp). */
	appliedHeight: number;
	/**
	 * Width before the call (state if set, otherwise the measured wrapper width).
	 * `null` only if the wrapper has not yet mounted.
	 */
	previousWidth: number | null;
	/**
	 * Height before the call (state if set, otherwise the measured wrapper height).
	 * `null` only if the wrapper has not yet mounted.
	 */
	previousHeight: number | null;
	/** True if the width axis actually grew as a result of the call. */
	widthGrew: boolean;
	/** True if the height axis actually grew as a result of the call. */
	heightGrew: boolean;
	/**
	 * True if the requested target width was larger than the current viewport
	 * could accommodate. Note: the applied value may still equal or exceed the
	 * current dimension because viewport clamping is never allowed to force a
	 * shrink.
	 */
	widthClampedByViewport: boolean;
	/**
	 * True if the requested target height was larger than the current viewport
	 * could accommodate. Note: the applied value may still equal or exceed the
	 * current dimension because viewport clamping is never allowed to force a
	 * shrink.
	 */
	heightClampedByViewport: boolean;
	/**
	 * `true` when the hook is enabled and at least one axis was applied.
	 * `false` when the hook is disabled (no-op) or both axes already met or
	 * exceeded the target (no-op due to don't-shrink rule).
	 */
	didChange: boolean;
	/** Echo of {@link SetSizeArgs.source} for downstream analytics convenience. */
	source?: string;
}

export interface UseResizeReturn {
	/**
	 * Ref to attach to the wrapper element
	 */
	wrapperRef: React.RefObject<HTMLDivElement>;
	/**
	 * Inline style object for the wrapper element
	 */
	wrapperStyle: React.CSSProperties | undefined;
	/**
	 * Resize handlers component that renders all resize handles (top, left, corner)
	 */
	ResizeHandlers: React.ComponentType;
	/**
	 * Whether the resize experiment is enabled
	 */
	isResizeEnabled: boolean;
	/**
	 * Controls greeting visibility based on resize state:
	 * - `null`: User hasn't resized yet — consumer should use default visibility.
	 * - `true`: User has resized to at least GREETING_HEADER_HEIGHT_THRESHOLD (700px) — show greeting.
	 * - `false`: User has resized below the threshold — hide greeting.
	 */
	isResizeHeaderThresholdReached: boolean | null;
	/**
	 * Programmatically resize the wrapper toward the requested target dimensions.
	 *
	 * Behaviour:
	 * - **No-op when the hook is disabled.** Returns a result with `didChange: false`.
	 * - **Per-axis don't-shrink.** Each axis is grown independently
	 *   (`appliedW = max(currentW, targetW)`, same for height), so calls that
	 *   would shrink either axis are silently ignored on that axis.
	 * - **Viewport-clamped, never shrinks.** The applied dimensions are capped
	 *   by `calculateMaxWidth()` / `calculateMaxHeight()` (which respect
	 *   viewport bounds and the configured offsets / safety margins). The
	 *   viewport cap can prevent further growth, but it will never force a
	 *   shrink below the current rendered dimension. Applied dimensions also
	 *   never fall below `minWidth` / `minHeight`.
	 * - **`hasUserResized` is set to `true`** when at least one axis grows, so
	 *   the greeting-collapse threshold logic treats the call the same as a
	 *   manual drag.
	 *
	 * Designed to be safe to call from a `useEffect` — the function identity is
	 * stable for the lifetime of the hook.
	 */
	setSize: (args: SetSizeArgs) => SetSizeResult;
}

const resizeHandleStyles = cssMap({
	topResizeHandle: {
		width: '100%',
		height: '12px',
		position: 'absolute',
		top: 0,
		left: 0,
		right: 0,
		cursor: 'row-resize',
		border: 'none',
		backgroundColor: 'transparent',
		touchAction: 'none',
	},
	topResizeIndicator: {
		position: 'absolute',
		height: '4px',
		width: '50px',
		top: token('space.0'),
		left: '50%',
		transform: 'translate(-50%, -50%)',
		backgroundColor: 'transparent',
		borderRadius: token('radius.xsmall'),
		transition: 'background-color 0.15s ease-out',
		pointerEvents: 'none',
	},
	topResizeIndicatorHovered: {
		backgroundColor: token('color.background.accent.gray.subtler'),
	},
	leftResizeHandle: {
		width: '12px',
		height: '100%',
		position: 'absolute',
		top: 0,
		left: 0,
		bottom: 0,
		cursor: 'col-resize',
		border: 'none',
		backgroundColor: 'transparent',
		touchAction: 'none',
	},
	leftResizeIndicator: {
		position: 'absolute',
		width: '4px',
		height: '50px',
		left: token('space.0'),
		top: '50%',
		transform: 'translate(-50%, -50%)',
		backgroundColor: 'transparent',
		borderRadius: token('radius.xsmall'),
		transition: 'background-color 0.15s ease-out',
		pointerEvents: 'none',
	},
	leftResizeIndicatorHovered: {
		backgroundColor: token('color.background.accent.gray.subtler'),
	},
	cornerResizeHandle: {
		width: '24px',
		height: '24px',
		position: 'absolute',
		top: 0,
		left: 0,
		cursor: 'nwse-resize',
		border: 'none',
		backgroundColor: 'transparent',
		touchAction: 'none',
	},
});

/**
 * Safety margin in pixels to prevent modal from reaching the bottom edge of the viewport
 */
const HEIGHT_SAFETY_MARGIN = 180;

/**
 * Minimum height in pixels at which to show the greeting header when user has resized the mini modal
 */
const GREETING_HEADER_HEIGHT_THRESHOLD = 700;

/**
 * Safety margin in pixels to prevent modal from reaching the right edge of the viewport
 */
const WIDTH_SAFETY_MARGIN = 100;

/**
 * Hook for handling resizing from top (vertical) and left (horizontal) borders.
 * - Top border: drag down decreases height, drag up increases height
 * - Left border: drag left increases width, drag right decreases width
 *
 * @param options - Configuration options for the resize hook
 * @returns Object containing ref, state, handlers, and styles for resizing
 */
export const useMiniModalResize = ({
	minHeight = 350,
	maxHeight,
	topOffset = 0,
	bottomOffset = HEIGHT_SAFETY_MARGIN,
	leftOffset = 0,
	maxWidth = 800,
	minWidth = 400,
	greetingHeight = 0,
	greetingKey = 'default',
	enableMiniModalResize = true,
}: UseResizeOptions = {}): UseResizeReturn => {
	// Only evaluate experiment when in mini modal mode to prevent over-exposures
	const enabled = useMemo(() => {
		if (!enableMiniModalResize && FeatureGates.checkGate('cc_ai_growth_welcome_from_rovo_gate')) {
			return false;
		}
		return (
			FeatureGates.getExperimentValue('rovo_button_floating_modal_resize', 'isEnabled', false) ||
			FeatureGates.checkGate('rovo_button_floating_modal_resize_extended') ||
			FeatureGates.getExperimentValue('cc-maui-experiment', 'isEnabled', false)
		);
	}, [enableMiniModalResize]);

	const { sendAnalyticsEvent } = useAnalytics();

	const [height, setHeight] = useState<number | null>(null);
	const [width, setWidth] = useState<number | null>(null);
	const [isResizing, setIsResizing] = useState<boolean>(false);
	const [hasUserResized, setHasUserResized] = useState<boolean>(false);
	const resizeModeRef = useRef<'vertical' | 'horizontal' | 'corner' | null>(null);
	const prevGreetingKeyRef = useRef<string | undefined>(greetingKey);
	// Mirror width/height state into refs so `setSize` (and any other callback)
	// can read the current dimensions without taking them as deps. This keeps
	// `setSize`'s identity stable for the lifetime of the hook, preventing
	// infinite render loops in consumers that follow the documented
	// `useEffect(() => { setSize(...); }, [setSize])` pattern.
	const widthRef = useRef<number | null>(null);
	const heightRef = useRef<number | null>(null);

	const wrapperRef = useRef<HTMLDivElement>(null);
	const isDraggingRef = useRef<boolean>(false);
	const hasMovedRef = useRef<boolean>(false);
	const initialPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
	const initialHeightRef = useRef<number>(0);
	const initialWidthRef = useRef<number>(0);
	const animationFrameRef = useRef<number | null>(null);
	const defaultMinHeightRef = useRef<number | null>(null);

	const topIndicatorRef = useRef<HTMLDivElement>(null);
	const leftIndicatorRef = useRef<HTMLDivElement>(null);
	const optionsRef = useRef({
		minHeight,
		maxHeight,
		maxWidth,
		minWidth,
		topOffset,
		bottomOffset,
		leftOffset,
		enabled,
		greetingHeight,
	});

	/**
	 * Keep options ref up to date for use in callbacks.
	 * This ensures that callbacks always have access to the latest option values
	 * without needing to include them in dependency arrays.
	 */
	useEffect(() => {
		if (!enabled) {
			return;
		}
		optionsRef.current = {
			minHeight,
			maxHeight,
			maxWidth,
			minWidth,
			topOffset,
			bottomOffset,
			leftOffset,
			enabled,
			greetingHeight,
		};
	}, [
		minHeight,
		maxHeight,
		maxWidth,
		minWidth,
		topOffset,
		bottomOffset,
		leftOffset,
		enabled,
		greetingHeight,
	]);

	/**
	 * Reset resize state when greeting context changes.
	 * This ensures greeting can use its default visibility when switching between contexts.
	 * We reset hasUserResized, height, and defaultMinHeightRef so the user starts fresh.
	 */
	useEffect(() => {
		if (!enabled) {
			return;
		}
		if (prevGreetingKeyRef.current !== greetingKey) {
			setHasUserResized(false);
			setHeight(null);
			defaultMinHeightRef.current = null;
		}
		prevGreetingKeyRef.current = greetingKey;
	}, [enabled, greetingKey]);

	// Keep width/height refs in sync with state so `setSize` can read the
	// current dimensions without depending on them (see ref declarations
	// above for why we mirror).
	useEffect(() => {
		widthRef.current = width;
	}, [width]);
	useEffect(() => {
		heightRef.current = height;
	}, [height]);

	/**
	 * Helper to set hover state on indicators without causing React re-renders.
	 * Directly manipulates the DOM to apply hover styles.
	 */
	const setIndicatorHover = useCallback((indicator: 'top' | 'left' | 'both' | null) => {
		const hoverColor = token('color.background.accent.gray.subtler');

		if (topIndicatorRef.current) {
			topIndicatorRef.current.style.backgroundColor =
				indicator === 'top' || indicator === 'both' ? hoverColor : 'transparent';
		}

		if (leftIndicatorRef.current) {
			leftIndicatorRef.current.style.backgroundColor =
				indicator === 'left' || indicator === 'both' ? hoverColor : 'transparent';
		}
	}, []);

	/**
	 * Gets the current dimensions of the wrapper element.
	 * Returns zero dimensions if the wrapper ref is not available.
	 *
	 * @returns Object containing height and width in pixels
	 */
	const getWrapperDimensions = useCallback(() => {
		if (!wrapperRef.current) {
			return { height: 0, width: 0 };
		}
		const rect = wrapperRef.current.getBoundingClientRect();
		return { height: rect.height || 0, width: rect.width || 0 };
	}, []);

	/**
	 * Calculates the maximum height allowed for resizing.
	 * Uses the provided maxHeight option if available, otherwise calculates
	 * based on viewport height minus top offset and a safety margin.
	 *
	 * @returns Maximum height in pixels
	 */
	const calculateMaxHeight = useCallback(() => {
		const {
			maxHeight: currentMaxHeight,
			topOffset: currentTopOffset,
			bottomOffset: currentBottomOffset,
		} = optionsRef.current;
		if (currentMaxHeight !== undefined) {
			return currentMaxHeight;
		}
		return window.innerHeight - currentTopOffset - currentBottomOffset;
	}, []);

	/**
	 * Calculates the maximum width allowed for resizing.
	 * Uses the provided maxWidth option if available, otherwise calculates
	 * based on viewport width minus left offset and a safety margin.
	 *
	 * @returns Maximum width in pixels
	 */
	const calculateMaxWidth = useCallback(() => {
		const { maxWidth: currentMaxWidth, leftOffset: currentLeftOffset } = optionsRef.current;
		if (currentMaxWidth !== undefined) {
			return currentMaxWidth;
		}
		return window.innerWidth - currentLeftOffset - WIDTH_SAFETY_MARGIN;
	}, []);

	/**
	 * Fires an analytics event when the resize operation is released.
	 */
	const fireOnEndResizeAnalyticsEvent = useCallback(() => {
		sendAnalyticsEvent({
			action: 'resized',
			actionSubject: 'miniModal',
			actionSubjectId: 'aiMatePanel',
			eventType: 'ui',
			attributes: {
				mode: resizeModeRef.current,
			},
		});
	}, [sendAnalyticsEvent]);

	/**
	 * Handles pointer movement during resize operation.
	 * Calculates the new dimensions based on the movement delta and applies
	 * min/max constraints. Uses requestAnimationFrame for smooth updates.
	 *
	 * @param currentX - Current X coordinate of the pointer
	 * @param currentY - Current Y coordinate of the pointer
	 */
	const handleMove = useCallback(
		(currentX: number, currentY: number) => {
			if (!isDraggingRef.current || !optionsRef.current.enabled || !resizeModeRef.current) {
				return;
			}

			const deltaX = currentX - initialPositionRef.current.x;
			const deltaY = currentY - initialPositionRef.current.y;

			// Clear queued animation frame
			if (animationFrameRef.current !== null) {
				cancelAnimationFrame(animationFrameRef.current);
			}

			animationFrameRef.current = requestAnimationFrame(() => {
				hasMovedRef.current = true;
				if (!isDraggingRef.current || !resizeModeRef.current) {
					return;
				}

				const calculatedMaxHeight = calculateMaxHeight();
				const calculatedMaxWidth = calculateMaxWidth();
				const { minHeight: currentMinHeight, minWidth: currentMinWidth } = optionsRef.current;

				if (resizeModeRef.current === 'vertical' || resizeModeRef.current === 'corner') {
					// When dragging down (positive distance), decreases height
					// When dragging up (negative distance), increases height
					const updatedHeight = initialHeightRef.current - deltaY;
					const clampedHeight = Math.max(
						Math.max(defaultMinHeightRef.current ?? 0, currentMinHeight),
						Math.min(calculatedMaxHeight, updatedHeight),
					);
					setHeight(clampedHeight);
				}

				if (resizeModeRef.current === 'horizontal' || resizeModeRef.current === 'corner') {
					// When dragging left (negative distance), increases width
					// When dragging right (positive distance), decreases width
					const updatedWidth = initialWidthRef.current - deltaX;
					const clampedWidth = Math.max(
						currentMinWidth,
						Math.min(calculatedMaxWidth, updatedWidth),
					);
					setWidth(clampedWidth);
				}
			});
		},
		[calculateMaxHeight, calculateMaxWidth],
	);

	/**
	 * Wrapper for pointer move events. Required because the window bind() listener
	 * needs a typed PointerEvent parameter; delegates to the generic handleMove.
	 */
	const handlePointerMove = useCallback(
		(event: PointerEvent) => {
			handleMove(event.clientX, event.clientY);
		},
		[handleMove],
	);

	/**
	 * Initializes a resize operation by storing the starting position and dimensions.
	 * Sets up the resize mode and marks the operation as active.
	 *
	 * @param startX - Initial X coordinate of the pointer
	 * @param startY - Initial Y coordinate of the pointer
	 * @param mode - Resize mode: 'vertical', 'horizontal', or 'corner'
	 */
	const startResize = useCallback(
		(startX: number, startY: number, mode: 'vertical' | 'horizontal' | 'corner') => {
			if (!optionsRef.current.enabled || !wrapperRef.current) {
				return;
			}
			isDraggingRef.current = true;
			setIsResizing(true);
			setHasUserResized(true);
			resizeModeRef.current = mode;
			initialPositionRef.current = { x: startX, y: startY };
			const dimensions = getWrapperDimensions();
			initialHeightRef.current = dimensions.height;
			initialWidthRef.current = dimensions.width;
			// Capture default min height, subtracting greetingHeight for collapsible content.
			// This allows users to resize below the initial height to collapse the greeting.
			if (defaultMinHeightRef.current === null && dimensions.height > 0) {
				defaultMinHeightRef.current = dimensions.height - (optionsRef.current.greetingHeight ?? 0);
			}

			// Keep indicators visible during drag
			const indicatorMap = {
				vertical: 'top',
				horizontal: 'left',
				corner: 'both',
			} as const;
			setIndicatorHover(indicatorMap[mode]);
		},
		[getWrapperDimensions, setIndicatorHover],
	);

	/**
	 * Handles pointer down event on the top resize handle.
	 * Initiates vertical resizing from the top border.
	 * Uses setPointerCapture to ensure all subsequent pointer events are routed
	 * to this element, preventing conflicts with underlying surfaces (e.g., whiteboards).
	 *
	 * @param event - Pointer down event
	 */
	const handleTopPointerDown = useCallback(
		(event: React.PointerEvent) => {
			if (!optionsRef.current.enabled) {
				return;
			}
			event.preventDefault();
			event.currentTarget.setPointerCapture(event.pointerId);
			startResize(event.clientX, event.clientY, 'vertical');
		},
		[startResize],
	);

	/**
	 * Handles pointer down event on the left resize handle.
	 * Initiates horizontal resizing from the left border.
	 *
	 * @param event - Pointer down event
	 */
	const handleLeftPointerDown = useCallback(
		(event: React.PointerEvent) => {
			if (!optionsRef.current.enabled) {
				return;
			}
			event.preventDefault();
			event.currentTarget.setPointerCapture(event.pointerId);
			startResize(event.clientX, event.clientY, 'horizontal');
		},
		[startResize],
	);

	/**
	 * Handles pointer down event on the corner resize handle.
	 * Initiates resizing of both dimensions simultaneously from the top-left corner.
	 *
	 * @param event - Pointer down event
	 */
	const handleCornerPointerDown = useCallback(
		(event: React.PointerEvent) => {
			if (!optionsRef.current.enabled) {
				return;
			}
			event.preventDefault();
			event.currentTarget.setPointerCapture(event.pointerId);
			startResize(event.clientX, event.clientY, 'corner');
		},
		[startResize],
	);

	/**
	 * Ends the resize operation by resetting all resize-related state.
	 * Called when the user releases the pointer or the browser cancels the gesture.
	 *
	 * Guards against:
	 * - Double-fire: pointerup and pointercancel can both fire in some browser/tablet scenarios.
	 *   The isDraggingRef check ensures cleanup runs only once.
	 * - Zero-movement false positives: analytics only fire if actual movement occurred (hasMovedRef).
	 */
	const handleEnd = useCallback(() => {
		if (!isDraggingRef.current) {
			return; // already cleaned up — prevents double-fire on pointerup + pointercancel
		}
		if (hasMovedRef.current) {
			fireOnEndResizeAnalyticsEvent(); // only fire if actual movement occurred
		}
		isDraggingRef.current = false;
		hasMovedRef.current = false; // reset for next drag
		setIsResizing(false);
		resizeModeRef.current = null;
		// Clear hover indicators when drag ends
		setIndicatorHover(null);
	}, [fireOnEndResizeAnalyticsEvent, setIndicatorHover]);

	/**
	 * Wrapper for pointer up events that delegates to handleEnd.
	 */
	const handlePointerUp = useCallback(() => {
		handleEnd();
	}, [handleEnd]);

	/**
	 * Shared handler for pointer leave events on resize handles.
	 * Only clears hover state if not actively resizing.
	 */
	const handlePointerLeaveIfNotResizing = useCallback(() => {
		if (!isResizing) {
			setIndicatorHover(null);
		}
	}, [isResizing, setIndicatorHover]);

	/**
	 * Cleanup effect that cancels any pending animation frames on unmount.
	 * Prevents memory leaks and ensures no updates occur after component unmounts.
	 */
	useEffect(() => {
		if (!enabled) {
			return;
		}
		return () => {
			if (animationFrameRef.current !== null) {
				cancelAnimationFrame(animationFrameRef.current);
			}
		};
	}, [enabled]);

	/**
	 * Binds pointer event handlers to the window when actively resizing.
	 * This allows resizing to continue even when the pointer moves outside the component.
	 * Includes pointercancel handler to clean up when the browser takes over the gesture
	 * (e.g., scrolling on tablet). Handlers are automatically cleaned up when resizing
	 * ends or component unmounts.
	 */
	useEffect(() => {
		if (!enabled || !isResizing) {
			return;
		}
		const pointerMoveHandler = bind(window, {
			type: 'pointermove',
			listener: handlePointerMove,
			options: { passive: true },
		});
		const pointerUpHandler = bind(window, {
			type: 'pointerup',
			listener: handlePointerUp,
			options: { passive: true },
		});
		const pointerCancelHandler = bind(window, {
			type: 'pointercancel',
			listener: handlePointerUp,
			options: { passive: true },
		});

		return () => {
			pointerMoveHandler();
			pointerUpHandler();
			pointerCancelHandler();
			if (animationFrameRef.current !== null) {
				cancelAnimationFrame(animationFrameRef.current);
			}
		};
	}, [handlePointerMove, handlePointerUp, enabled, isResizing]);

	/**
	 * Computes the wrapper style object based on current resize dimensions.
	 * Returns undefined if resizing is disabled or no dimensions have been set.
	 * Only includes height/width properties that have been explicitly set.
	 *
	 * @returns Style object with height and/or width, or undefined
	 */
	const wrapperStyle: React.CSSProperties | undefined = useMemo(() => {
		if (!enabled) {
			return undefined;
		}
		const styles: React.CSSProperties = {};
		if (height !== null) {
			styles.height = `${height}px`;
		}
		if (width !== null) {
			styles.width = `${width}px`;
		}
		return Object.keys(styles).length > 0 ? styles : undefined;
	}, [enabled, height, width]);

	/**
	 * React component for the top resize handle.
	 * Renders a horizontal bar at the top of the container for vertical resizing.
	 * Uses direct DOM manipulation for hover states to avoid re-renders.
	 *
	 * @returns Box component configured for top border resizing
	 */
	const TopResizeHandler = useCallback(() => {
		const isVerticalResizing = isResizing && resizeModeRef.current === 'vertical';

		return (
			<Box
				onPointerDown={handleTopPointerDown}
				onPointerEnter={() => setIndicatorHover('top')}
				onPointerLeave={handlePointerLeaveIfNotResizing}
				testId="resize-handle-top"
				data-dragging={isVerticalResizing ? 'true' : undefined}
				xcss={resizeHandleStyles.topResizeHandle}
			>
				<Box ref={topIndicatorRef} xcss={resizeHandleStyles.topResizeIndicator} />
			</Box>
		);
	}, [handleTopPointerDown, handlePointerLeaveIfNotResizing, isResizing, setIndicatorHover]);

	/**
	 * React component for the left resize handle.
	 * Renders a vertical bar on the left side of the container for horizontal resizing.
	 * Uses direct DOM manipulation for hover states to avoid re-renders.
	 *
	 * @returns Box component configured for left border resizing
	 */
	const LeftResizeHandler = useCallback(() => {
		const isHorizontalResizing = isResizing && resizeModeRef.current === 'horizontal';

		return (
			<Box
				onPointerDown={handleLeftPointerDown}
				onPointerEnter={() => setIndicatorHover('left')}
				onPointerLeave={handlePointerLeaveIfNotResizing}
				testId="resize-handle-left"
				data-dragging={isHorizontalResizing ? 'true' : undefined}
				xcss={resizeHandleStyles.leftResizeHandle}
			>
				<Box ref={leftIndicatorRef} xcss={resizeHandleStyles.leftResizeIndicator} />
			</Box>
		);
	}, [handleLeftPointerDown, handlePointerLeaveIfNotResizing, isResizing, setIndicatorHover]);

	/**
	 * React component for the corner resize handle.
	 * Renders a small square at the top-left corner for resizing both dimensions simultaneously.
	 * When hovering over corner, both top and left indicators show hover state via direct DOM manipulation.
	 *
	 * @returns Box component configured for corner resizing
	 */
	const CornerResizeHandler = useCallback(() => {
		const isCornerResizing = isResizing && resizeModeRef.current === 'corner';
		return (
			<Box
				onPointerDown={handleCornerPointerDown}
				onPointerEnter={() => setIndicatorHover('both')}
				onPointerLeave={handlePointerLeaveIfNotResizing}
				testId="resize-handle-corner"
				data-dragging={isCornerResizing ? 'true' : undefined}
				xcss={resizeHandleStyles.cornerResizeHandle}
			/>
		);
	}, [handleCornerPointerDown, handlePointerLeaveIfNotResizing, isResizing, setIndicatorHover]);

	/**
	 * React component that renders all resize handlers (top, left, and corner).
	 * This component should be rendered as the **last child** of the element being resized
	 * to ensure handlers appear on top via natural DOM paint order without z-index.
	 * Uses direct DOM manipulation for hover effects to prevent component re-renders.
	 *
	 * @returns Fragment containing all resize handler components
	 */
	const ResizeHandlers = useCallback(() => {
		if (!enabled) {
			return null;
		}
		return (
			<>
				<TopResizeHandler />
				<LeftResizeHandler />
				<CornerResizeHandler />
			</>
		);
	}, [enabled, TopResizeHandler, LeftResizeHandler, CornerResizeHandler]);

	// Greeting visibility: null = not yet resized (use default), true/false = above/below threshold
	const isResizeHeaderThresholdReached: boolean | null =
		!enabled || !hasUserResized
			? null
			: height !== null && height >= GREETING_HEADER_HEIGHT_THRESHOLD;

	/**
	 * Programmatically resize the wrapper toward the requested target dimensions.
	 *
	 * Implementation notes:
	 * - The hook tracks `width` / `height` as `null` until the user has manually
	 *   resized at least once (or the modal has been auto-resized once). When
	 *   either is `null`, fall back to the measured wrapper dimensions so the
	 *   per-axis don't-shrink rule still works on the very first call (e.g.
	 *   when auto-expand fires on a freshly-mounted modal).
	 * - Width / height state is only updated on axes that actually grew, so the
	 *   no-op case (caller-requested target is already met or exceeded on both
	 *   axes) does not dirty React state and does not flip `hasUserResized`.
	 */
	const setSize = useCallback(
		({ targetWidth, targetHeight, source, dryRun = false }: SetSizeArgs): SetSizeResult => {
			// Read width/height through refs (not state) so `setSize` keeps a
			// stable identity for the lifetime of the hook (see widthRef /
			// heightRef declarations for context).
			const currentStateWidth = widthRef.current;
			const currentStateHeight = heightRef.current;

			// Measure the wrapper once — used both as a fallback when state is
			// null and as the source of truth for the disabled `noOp` return so
			// analytics callers see a meaningful currently-rendered size rather
			// than 0x0 (PR feedback: fix #4).
			const measured = getWrapperDimensions();
			const fallbackWidth = measured.width > 0 ? measured.width : 0;
			const fallbackHeight = measured.height > 0 ? measured.height : 0;

			const noOp = (): SetSizeResult => ({
				appliedWidth: currentStateWidth ?? fallbackWidth,
				appliedHeight: currentStateHeight ?? fallbackHeight,
				previousWidth: currentStateWidth,
				previousHeight: currentStateHeight,
				widthGrew: false,
				heightGrew: false,
				widthClampedByViewport: false,
				heightClampedByViewport: false,
				didChange: false,
				source,
			});

			if (!optionsRef.current.enabled) {
				return noOp();
			}

			// Resolve current dimensions: prefer state, fall back to measured wrapper.
			const currentWidth = currentStateWidth ?? (measured.width > 0 ? measured.width : null);
			const currentHeight = currentStateHeight ?? (measured.height > 0 ? measured.height : null);

			const { minWidth: currentMinWidth, minHeight: currentMinHeight } = optionsRef.current;
			const calculatedMaxWidth = calculateMaxWidth();
			const calculatedMaxHeight = calculateMaxHeight();

			// Per-axis don't-shrink rule. When current is null (wrapper not yet
			// mounted), treat as "smaller than any target" so we always grow.
			// We also floor by the configured min here (rather than at the end) so
			// the min-bound correction is treated as part of "where we want to be"
			// and not as a growth event — otherwise a sub-min current dimension
			// would falsely flag `widthGrew`/`heightGrew` even when the caller
			// requested no growth.
			const desiredWidth = Math.max(currentWidth ?? 0, targetWidth, currentMinWidth);
			const desiredHeight = Math.max(currentHeight ?? 0, targetHeight, currentMinHeight);

			// Viewport clamp, bounded below by the current dimension so the viewport
			// can prevent further growth but never force a shrink past what the user
			// is already showing. If the wrapper has not measured yet, the viewport
			// max is used as-is.
			const safeMaxWidth =
				currentWidth !== null ? Math.max(currentWidth, calculatedMaxWidth) : calculatedMaxWidth;
			const safeMaxHeight =
				currentHeight !== null ? Math.max(currentHeight, calculatedMaxHeight) : calculatedMaxHeight;

			// Final applied value: desired (already floored by min) clamped by the
			// viewport-bounded max.
			const appliedWidth = Math.min(safeMaxWidth, desiredWidth);
			const appliedHeight = Math.min(safeMaxHeight, desiredHeight);

			// Did either axis actually move? We compare against the *effective* current
			// dimension — the larger of the rendered value and the configured min —
			// so a min-bound correction (e.g. wrapper measured at 300 while min is
			// 350) is not mistaken for caller-driven growth.
			// `currentWidth === null` means the wrapper hasn't measured yet — treat
			// any applied value as a growth so the modal does adopt the new size.
			const effectiveCurrentWidth =
				currentWidth === null ? null : Math.max(currentWidth, currentMinWidth);
			const effectiveCurrentHeight =
				currentHeight === null ? null : Math.max(currentHeight, currentMinHeight);
			const widthGrew =
				effectiveCurrentWidth === null ? appliedWidth > 0 : appliedWidth > effectiveCurrentWidth;
			const heightGrew =
				effectiveCurrentHeight === null
					? appliedHeight > 0
					: appliedHeight > effectiveCurrentHeight;

			// Dry-run callers (e.g. the auto-expand Control-exposure probe) want the
			// computed result without mutating state. Skip all writes below.
			if (dryRun) {
				return {
					appliedWidth,
					appliedHeight,
					previousWidth: currentWidth,
					previousHeight: currentHeight,
					widthGrew,
					heightGrew,
					widthClampedByViewport: targetWidth > calculatedMaxWidth,
					heightClampedByViewport: targetHeight > calculatedMaxHeight,
					didChange: widthGrew || heightGrew,
					source,
				};
			}

			if (widthGrew) {
				setWidth(appliedWidth);
				// Also update the ref synchronously so a follow-up call within the
				// same tick sees the new value (without waiting for the mirror
				// effect to commit).
				widthRef.current = appliedWidth;
			}
			if (heightGrew) {
				setHeight(appliedHeight);
				heightRef.current = appliedHeight;
				// PR feedback fix #3: when the auto-expand grows the height for the
				// first time, anchor `defaultMinHeightRef` to the *pre-grow* height
				// (minus greeting offset). Otherwise the first subsequent manual
				// drag uses the post-auto-expand height as its lower bound and the
				// user cannot drag back below the auto-expanded size.
				if (defaultMinHeightRef.current === null && currentHeight !== null) {
					defaultMinHeightRef.current = currentHeight - (optionsRef.current.greetingHeight ?? 0);
				}
			}
			if (widthGrew || heightGrew) {
				setHasUserResized(true);
			}

			return {
				appliedWidth,
				appliedHeight,
				previousWidth: currentWidth,
				previousHeight: currentHeight,
				widthGrew,
				heightGrew,
				// PR feedback fix #2: compare against the *requested* target, not
				// the don't-shrink-adjusted `desiredWidth`. The JSDoc contract is
				// "true if the requested target width was larger than the viewport
				// could accommodate" — clamping should not be reported when the
				// modal merely happens to already be wider than the viewport.
				widthClampedByViewport: targetWidth > calculatedMaxWidth,
				heightClampedByViewport: targetHeight > calculatedMaxHeight,
				didChange: widthGrew || heightGrew,
				source,
			};
		},
		[calculateMaxHeight, calculateMaxWidth, getWrapperDimensions],
	);

	return {
		wrapperRef,
		wrapperStyle,
		ResizeHandlers,
		isResizeEnabled: enabled,
		isResizeHeaderThresholdReached,
		setSize,
	};
};
